/**
 * A two-column label/value grid — the shape the admin console already uses for a
 * user's attributes, promoted so more than one page can wear it.
 *
 * Why a grid and not a form: these are FACTS about a record, read far more often
 * than they are changed. Stacking them as form rows makes reading a scroll;
 * pairing them two-up keeps the whole record on one screen, and an uppercase
 * label above its value scans as a caption rather than competing with it.
 *
 * A value may still be editable — pass an input as `children` — but the grid
 * stays a grid, so an editable field sits in the same place its read-only
 * neighbour does.
 *
 * The visual is lifted from `UserDetail.css`'s `.user-attributes`, deliberately
 * unchanged so the two surfaces look like one system. That file still carries its
 * own copy for its own markup; migrating it onto this component is the obvious
 * follow-up and the reason the class names here are prefixed rather than
 * identical — two definitions of `.attr-row` loaded together would be a silent
 * collision.
 */
import type { ReactNode } from 'react';
import './AttributeGrid.css';

export function AttributeGrid({ children, columns = 2 }: {
  children: ReactNode;
  /** Two by default. One is for a narrow column where pairs would wrap anyway. */
  columns?: 1 | 2;
}) {
  return (
    <div className={`sui-attrs sui-attrs--${columns}`}>{children}</div>
  );
}

export function Attribute({ label, children, span, mono }: {
  label: string;
  /** A string renders as the value; anything else renders as given, so an input
   *  or a badge sits exactly where a plain value would. */
  children: ReactNode;
  /** Take the full width — for a paragraph, which reads badly in half a row. */
  span?: boolean;
  mono?: boolean;
}) {
  return (
    <div className={`sui-attr-row${span ? ' sui-attr-row--span' : ''}`}>
      <span className="sui-attr-label">{label}</span>
      {typeof children === 'string' || typeof children === 'number'
        ? <span className={`sui-attr-value${mono ? ' sui-attr-mono' : ''}`}>{children}</span>
        : children}
    </div>
  );
}
